<template>
  <div class="flex flex-col rounded bg-pure-white p-4 lg:p-5">
    <div v-if="!editOpened" class="mb-4">
      <BlTextBody2
        v-if="!comment.type"
        :text="comment.body"
        class="text-slate-1000 whitespace-pre-line"
      />
      <div v-else class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <BlTextBody2 text="Antecedent" />
          <BlTextBody2
            :text="comment.antecedent || '-'"
            class="text-slate-1000 whitespace-pre-line"
          />
        </div>
        <div class="flex flex-col gap-1">
          <BlTextBody2 text="Behavior" />
          <BlTextBody2
            :text="comment.behavior || '-'"
            class="text-slate-1000 whitespace-pre-line"
          />
        </div>
        <div class="flex flex-col gap-1">
          <BlTextBody2 text="Consequence" />
          <BlTextBody2
            :text="comment.consequence || '-'"
            class="text-slate-1000 whitespace-pre-line"
          />
        </div>
      </div>
    </div>
    <div v-else class="mb-4">
      <BlInputText
        v-if="!comment.type"
        :input="bodyInput"
        @changed="inputHandler"
      />
      <div v-else class="flex flex-col gap-4">
        <BlInputText :input="antecedentInput" @changed="inputHandler" />
        <BlInputText :input="behaviorInput" @changed="inputHandler" />
        <BlInputText :input="consequenceInput" @changed="inputHandler" />
      </div>
    </div>
    <!--  -->
    <BlTextBody2
      v-if="comment.target_name"
      :text="comment.target_name"
      class="whitespace-pre-line mb-2"
    />
    <!--  -->
    <div
      v-if="!editOpened"
      class="pt-2 flex items-center justify-between gap-2 border-t border-slate-400"
    >
      <div class="flex items-center gap-2 truncate">
        <div v-if="!comment.measurement_id">
          <BlTextBody2
            v-if="!comment.user_name"
            title="Deleted user"
            text="Deleted user"
            class="italic truncate"
          />
          <div
            v-else
            class="h-8 px-2 flex items-center rounded bg-success-100 lg:flex-shrink-0 border border-success-100 lg:max-w-40 truncate"
          >
            <BlTextBody2
              :title="comment.user_name"
              :text="comment.user_name"
              class="text-success truncate"
            />
          </div>
        </div>
        <div
          v-if="viewMode === 'desktop'"
          class="h-8 px-2 flex items-center gap-2 rounded bg-slate-200 flex-shrink-0 border border-slate-200"
        >
          <BlTextBody2 :text="displayDate(comment.updated_at, 'DD/MM/YYYY')" />
          <div class="h-2 w-2 flex-shrink-0 rounded-full bg-slate-500"></div>
          <BlTextBody2 :text="displayDate(comment.updated_at, 'HH:mm')" />
        </div>
        <BlTextBody2
          v-if="comment.is_edited && viewMode === 'desktop'"
          text="Edited"
        />
      </div>
      <div
        v-if="comment.user_id === currentUser.id && !disabledAction"
        class="flex items-center gap-2 flex-shrink-0"
      >
        <BlInputButton
          v-if="!comment.target_name"
          type="outlined-error"
          dense
          icon="delete"
          label="Delete"
          @clicked="deleteOpened = true"
        />
        <BlInputButton
          v-if="!comment.target_name"
          type="outlined"
          dense
          icon="edit"
          label="Edit"
          @clicked="openEdit"
        />
        <BlInputButton
          v-if="comment.target_name"
          type="outlined"
          dense
          icon="edit"
          label="Edit target comment"
          @clicked="openEditCommentTarget"
        />
      </div>
    </div>
    <div
      v-else
      class="pt-2 flex items-center justify-end border-t border-slate-400"
    >
      <div class="flex items-center gap-2">
        <BlInputButton
          type="outlined"
          dense
          label="Cancel"
          @clicked="editOpened = false"
        />
        <BlInputButton
          v-if="!comment.target_name"
          label="Update"
          dense
          :disabled="isDisabledSubmit"
          :loading="editLoading"
          @clicked="onUpdate"
        />
        <BlInputButton
          v-if="comment.target_name"
          label="Update target comment"
          dense
          :disabled="isDisabledSubmit"
          :loading="editLoading"
          @clicked="onUpdateCommentTarget"
        />
      </div>
    </div>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete comment"
      description="You are about to delete the comment. Are you sure you want to proceed? This action is irreversible."
      label="Delete"
      type="error"
      empty
      :disabled="deleteLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onDelete"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import moment from "moment";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "SessionCommentItem",
  components: { BlTextBody2, BlInputText, BlInputButton, BlBackdrop },
  props: {
    comment: { type: Object, required: true },
    disabledAction: { type: Boolean, default: false }
    // @on_filter
  },
  data() {
    return {
      editOpened: false,
      editLoading: false,
      bodyInput: {
        model: "",
        placeholder: "Type your comment",
        textarea: true
      },
      antecedentInput: {
        label: "Antecedent",
        model: "",
        placeholder: "Type your comment",
        textarea: true
      },
      behaviorInput: {
        label: "Behavior",
        model: "",
        placeholder: "Type your comment",
        textarea: true
      },
      consequenceInput: {
        label: "Consequence",
        model: "",
        placeholder: "Type your comment",
        textarea: true
      },
      deleteOpened: false,
      deleteLoading: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "session", "viewMode"]),
    isDisabledSubmit() {
      if (!this.comment.type) {
        if (!this.bodyInput.model) return true;
        if (this.bodyInput.model === this.comment.body) return true;
      } else {
        if (
          !this.antecedentInput.model &&
          !this.behaviorInput.model &&
          !this.consequenceInput.model
        ) {
          return true;
        }
        if (
          this.antecedentInput.model === this.comment.antecedent &&
          this.behaviorInput.model === this.comment.behavior &&
          this.consequenceInput.model === this.comment.consequence
        ) {
          return true;
        }
      }
      return false;
    }
  },
  mounted() {},
  methods: {
    ...mapMutations(["setSessionComment"]),
    ...mapActions([
      "updateSessionComment",
      "deleteSessionComment",
      "updateAssessment",
      "deleteAssessment",
      "updateMeasurement",
      "showToast"
    ]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    displayDate(date, format) {
      if (!date) return "-";
      return moment(date).format(format);
    },
    openEdit() {
      if (!this.comment.type) {
        this.bodyInput.model = this.comment.body;
      } else {
        this.antecedentInput.model = this.comment.antecedent;
        this.behaviorInput.model = this.comment.behavior;
        this.consequenceInput.model = this.comment.consequence;
      }
      this.editOpened = true;
    },
    openEditCommentTarget() {
      this.bodyInput.model = this.comment.body;
      this.editOpened = true;
    },
    onUpdate() {
      this.editLoading = true;
      if (!this.comment.type) {
        const data = {
          id: this.comment.id,
          session_comment: {
            body: this.bodyInput.model
          }
        };
        this.updateSessionComment(data).then(({ success }) => {
          this.editLoading = false;
          if (!success) return;
          this.editOpened = false;
        });
      } else {
        const data = {
          id: this.session.client_id,
          assessment: {
            id: this.comment.id,
            antecedent: this.antecedentInput.model,
            behavior: this.behaviorInput.model,
            consequence: this.consequenceInput.model,
            type: "Assessment::InSession"
          }
        };
        this.updateAssessment(data).then(({ success }) => {
          this.editLoading = false;
          if (!success) return;
          this.editOpened = false;
        });
      }
    },
    onUpdateCommentTarget() {
      this.editLoading = true;
      const data = {
        id: this.comment.measurement_id,
        measurement: {
          comment: this.bodyInput.model
        }
      };
      this.updateMeasurement(data).then(({ success }) => {
        this.editLoading = false;
        if (!success) return;
        const title = `Comment updated`;
        const message = `Success! The comment has been updated.`;
        this.showToast({ title, message, type: "success" });
        this.setSessionComment({ ...this.comment, body: this.bodyInput.model });
        this.editOpened = false;
      });
    },
    onDelete() {
      this.deleteLoading = true;
      if (!this.comment.type) {
        const data = {
          id: this.comment.id
        };
        this.deleteSessionComment(data).then(({ success }) => {
          this.deleteLoading = false;
          if (!success) return;
          this.deleteOpened = false;
        });
      } else {
        const data = {
          id: this.session.client_id,
          assessment: {
            id: this.comment.id,
            type: "Assessment::InSession"
          }
        };
        this.deleteAssessment(data).then(({ success }) => {
          this.deleteLoading = false;
          if (!success) return;
          this.deleteOpened = false;
        });
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/sessions/comment/Item.vue